import type { PolicyPeriodStatus } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { OnlineSales } from 'gw-edge-client';
import { useRecoilValue } from 'recoil';
import { useAsyncThrow } from './useAsyncThrow';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { allCoverablesAtom } from '../recoil/atoms/allCoverablesAtom';
import { allCoverablesToCoverableDTOArray } from '../utils/coverablesUtil';

const RETRY_TIMEOUT = 1000;
const MAX_RETRIES = 10;
const MAX_INCREASE_RATE = 16;

const wait = async (duration: number): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, duration));
};

const canProceedToReceiptPage = (
    isPendingCompletion: boolean,
    status: PolicyPeriodStatus,
    allowProceedingForQuoted: boolean
): boolean => {
    return (
        (isPendingCompletion && status === 'Quoted') ||
        (!isPendingCompletion && status === 'Bound') ||
        (!isPendingCompletion && status === 'Quoted' && allowProceedingForQuoted)
    );
};

export function useAwaitBoundJob(isPendingCompletion: boolean): {
    awaitBoundJob: () => Promise<void>;
} {
    const { asyncThrow } = useAsyncThrow();
    const { jobNumber, sessionId } = useRecoilValue(jobInfoAtom);

    const allCoverables = useRecoilValue(allCoverablesAtom);
    const coverables = allCoverablesToCoverableDTOArray(allCoverables);
    const allowProceedingForQuoted = coverables.every(
        (cov) => cov.willSpinOff || (cov.status === 'Removed' && cov.lobCode === 'MVP')
    );

    const awaitBoundJob = async (): Promise<void> => {
        let retries = 0;
        let status = await OnlineSales.getJobStatus(jobNumber, sessionId);

        if (canProceedToReceiptPage(isPendingCompletion, status, allowProceedingForQuoted)) return;

        while (retries <= MAX_RETRIES) {
            const exponentialBackoffRate = Math.pow(2, retries);
            const delayIncreaseRate =
                exponentialBackoffRate > MAX_INCREASE_RATE
                    ? MAX_INCREASE_RATE
                    : exponentialBackoffRate;
            await wait(RETRY_TIMEOUT * delayIncreaseRate);

            try {
                status = await OnlineSales.getJobStatus(jobNumber, sessionId);
            } catch (err) {
                if (err instanceof Error) asyncThrow(err);
            }

            if (canProceedToReceiptPage(isPendingCompletion, status, allowProceedingForQuoted)) {
                break;
            }

            retries++;
        }

        if (!canProceedToReceiptPage(isPendingCompletion, status, allowProceedingForQuoted)) {
            asyncThrow(new Error('Exceeded max retries for awaitBoundJob.'));
        }
    };

    return { awaitBoundJob };
}
